import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import waitTimeManager from './WaitTimeManager';

class AppLifecycleManager {
  constructor() {
    this.isInitialized = false;
    this.beforeUnloadHandler = null;
    this.visibilityChangeHandler = null;
    this.isAppVisible = true;
    this.heartbeatInterval = null;
    this.cloudFunctionListener = null;
    
    this.initializeHandlers = this.initializeHandlers.bind(this);
    this.cleanup = this.cleanup.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.sendHeartbeat = this.sendHeartbeat.bind(this);
    this.checkCloudFunctionStatus = this.checkCloudFunctionStatus.bind(this);
  }

  // App-Lifecycle-Handlers initialisieren
  initializeHandlers() {
    if (this.isInitialized) {
      console.warn('AppLifecycleManager: Already initialized');
      return;
    }

    // Before-Unload Handler für Cleanup bei App-Schließung
    this.beforeUnloadHandler = (event) => {
      this.handleBeforeUnload(event);
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Visibility-Change Handler für Tab-Wechsel
    this.visibilityChangeHandler = () => {
      this.handleVisibilityChange();
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Heartbeat für App-Status alle 30 Sekunden
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 30000);

    // Cloud-Funktion-Status überwachen
    this.setupCloudFunctionListener();

    this.isInitialized = true;
    console.log('AppLifecycleManager: Handlers initialisiert');
  }

  // Cleanup bei App-Schließung
  async handleBeforeUnload(event) {
    try {
      console.log('AppLifecycleManager: App wird geschlossen, führe Cleanup durch...');
      
      // WaitTimeManager stoppen
      if (waitTimeManager.getStatus().isActive) {
        await waitTimeManager.stop();
      }
      
      // App-Status in Firebase deaktivieren
      await this.setAppStatus(false);
      
      console.log('AppLifecycleManager: Cleanup abgeschlossen');
      
    } catch (error) {
      console.error('AppLifecycleManager: Fehler beim Cleanup:', error);
    }
  }

  // Tab-Visibility-Change Handler
  async handleVisibilityChange() {
    const isVisible = !document.hidden;

    if (isVisible !== this.isAppVisible) {
      this.isAppVisible = isVisible;

      try {
        if (isVisible) {
          console.log('AppLifecycleManager: Tab wieder sichtbar');

          // Prüfe ob Cloud-Funktion läuft
          const cloudFunctionActive = await this.checkCloudFunctionStatus();

          if (!cloudFunctionActive && !waitTimeManager.getStatus().isActive) {
            // WaitTimeManager wieder starten wenn Cloud-Funktion nicht aktiv
            await waitTimeManager.start();
          }
        } else {
          console.log('AppLifecycleManager: Tab versteckt (App bleibt aktiv)');
        }

        // App bleibt aktiv, nur tabVisible ändert sich
        await this.setAppStatus(true);

      } catch (error) {
        console.error('AppLifecycleManager: Fehler bei Visibility-Change:', error);
      }
    }
  }

  // Heartbeat senden um App-Status zu aktualisieren
  async sendHeartbeat() {
    if (this.isInitialized) {
      try {
        await this.setAppStatus(true);
      } catch (error) {
        console.error('AppLifecycleManager: Fehler beim Heartbeat:', error);
      }
    }
  }

  // App-Status in Firebase setzen/aktualisieren
  async setAppStatus(isActive) {
    try {
      const statusDocRef = doc(db, 'appStatus', 'webApp');
      
      const statusData = {
        isActive,
        lastSeen: new Date(),
        calculatingWaitTimes: isActive && waitTimeManager.getStatus().isActive,
        tabVisible: this.isAppVisible,
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      await setDoc(statusDocRef, statusData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('AppLifecycleManager: App-Status aktualisiert:', statusData);
      }
      
    } catch (error) {
      console.warn('AppLifecycleManager: App-Status-Update fehlgeschlagen (möglicherweise keine Berechtigung):', error.message);
      // Ignoriere Permission-Fehler und arbeite ohne Status-Updates
    }
  }

  // Cloud-Funktion-Status prüfen
  async checkCloudFunctionStatus() {
    try {
      const cloudStatusDocRef = doc(db, 'appStatus', 'cloudFunction');
      const cloudStatusDoc = await getDoc(cloudStatusDocRef);
      
      if (cloudStatusDoc.exists()) {
        const data = cloudStatusDoc.data();
        const lastSeen = data.lastSeen?.toDate();
        const now = new Date();
        
        // Cloud-Funktion als aktiv betrachten wenn letzter Heartbeat < 5 Minuten
        const isActive = lastSeen && (now - lastSeen) < 300000;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('AppLifecycleManager: Cloud-Funktion Status:', {
            isActive,
            lastSeen,
            minutesAgo: lastSeen ? Math.round((now - lastSeen) / 60000) : null
          });
        }
        
        return isActive;
      }
      
      return false;
      
    } catch (error) {
      console.error('AppLifecycleManager: Fehler beim Cloud-Funktion-Status-Check:', error);
      return false;
    }
  }

  // Cloud-Funktion-Status Listener einrichten
  setupCloudFunctionListener() {
    try {
      const cloudStatusDocRef = doc(db, 'appStatus', 'cloudFunction');
      
      this.cloudFunctionListener = onSnapshot(cloudStatusDocRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const lastSeen = data.lastSeen?.toDate();
          const now = new Date();
          const isCloudFunctionActive = lastSeen && (now - lastSeen) < 300000;
          
          // Wenn Cloud-Funktion wieder aktiv wird, stoppe Web-App-Berechnung
          if (isCloudFunctionActive && waitTimeManager.getStatus().isActive) {
            console.log('AppLifecycleManager: Cloud-Funktion wieder aktiv, stoppe lokale Berechnung');
            waitTimeManager.stop();
          }
          
          // Wenn Cloud-Funktion inaktiv wird und Web-App aktiv ist, starte lokale Berechnung
          if (!isCloudFunctionActive && this.isAppVisible && this.isInitialized) {
            setTimeout(async () => {
              const stillInactive = !(await this.checkCloudFunctionStatus());
              if (stillInactive && !waitTimeManager.getStatus().isActive) {
                console.log('AppLifecycleManager: Cloud-Funktion inaktiv, starte lokale Berechnung');
                await waitTimeManager.start();
              }
            }, 30000); // 30 Sekunden warten um sicherzustellen dass Cloud-Funktion wirklich aus ist
          }
        }
      }, (error) => {
        console.warn('AppLifecycleManager: Cloud-Funktion-Listener Fehler (möglicherweise keine Berechtigung):', error.message);
        // Ignoriere Permission-Fehler und arbeite ohne Cloud-Funktion-Überwachung
      });
      
    } catch (error) {
      console.warn('AppLifecycleManager: Setup Cloud-Funktion-Listener fehlgeschlagen (möglicherweise keine Berechtigung):', error.message);
      // Ignoriere Permission-Fehler
    }
  }

  // Koordinierte Initialisierung der Wartezeitberechnung
  async initializeWaitTimeCalculation() {
    try {
      console.log('AppLifecycleManager: Initialisiere Wartezeitberechnung...');
      
      // Prüfe Cloud-Funktion-Status
      const isCloudFunctionActive = await this.checkCloudFunctionStatus();
      
      if (!isCloudFunctionActive) {
        console.log('AppLifecycleManager: Cloud-Funktion inaktiv, starte lokale Berechnung');
        
        // App-Status setzen
        await this.setAppStatus(true);
        
        // WaitTimeManager starten
        await waitTimeManager.start();
        
      } else {
        console.log('AppLifecycleManager: Cloud-Funktion aktiv, verwende deren Berechnungen');
        
        // App als aktiv markieren aber keine eigene Berechnung
        await this.setAppStatus(true);
      }
      
    } catch (error) {
      console.error('AppLifecycleManager: Fehler bei Wartezeitberechnung-Initialisierung:', error);
      throw error;
    }
  }

  // Cleanup aller Handler und Listener
  async cleanup() {
    try {
      console.log('AppLifecycleManager: Führe vollständigen Cleanup durch...');
      
      // Event-Listener entfernen
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.beforeUnloadHandler = null;
      }
      
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        this.visibilityChangeHandler = null;
      }
      
      // Heartbeat stoppen
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      
      // Cloud-Funktion-Listener stoppen
      if (this.cloudFunctionListener) {
        this.cloudFunctionListener();
        this.cloudFunctionListener = null;
      }
      
      // WaitTimeManager stoppen
      if (waitTimeManager.getStatus().isActive) {
        await waitTimeManager.stop();
      }
      
      // App-Status deaktivieren
      await this.setAppStatus(false);
      
      this.isInitialized = false;
      console.log('AppLifecycleManager: Cleanup abgeschlossen');
      
    } catch (error) {
      console.error('AppLifecycleManager: Fehler beim Cleanup:', error);
    }
  }

  // Status-Informationen abrufen
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isAppVisible: this.isAppVisible,
      waitTimeManagerActive: waitTimeManager.getStatus().isActive,
      hasEventListeners: Boolean(this.beforeUnloadHandler && this.visibilityChangeHandler),
      hasHeartbeat: Boolean(this.heartbeatInterval),
      hasCloudFunctionListener: Boolean(this.cloudFunctionListener)
    };
  }
}

// Singleton-Instanz erstellen
const appLifecycleManager = new AppLifecycleManager();

export default appLifecycleManager;